import axios from 'axios';
import {Form} from '@/plugins/FWForms/index';
import * as types from '../mutation-types';
import {push} from 'notivue';

export interface CustodianState {
    init: boolean;
    loading: Record<string, boolean>;
    form: Form<object>;
    custodian: any;
    invite: any;
    custodians: any[];
    listings: any[];
    bookings: any[];

}

const state: CustodianState = {
    init: false,
    loading: {
        create: false,
        delete: false,
        update: false,
        join: false,
    },
    form: new Form(),
    custodian: null,
    invite: null,
    custodians: [],
    listings: [],
    bookings: [],
};

const getters = {
    init: (state: CustodianState) => state.init,
    loading: (state: CustodianState) => state.loading,
    form: (state: CustodianState) => state.form,
    custodian: (state: CustodianState) => state.custodian,
    custodians: (state: CustodianState) => state.custodians,
    invite: (state: CustodianState) => state.invite,
    listings: (state: CustodianState) => state.listings,
    bookings: (state: CustodianState) => state.bookings,
};

const mutations = {
    [types.SET_CUSTODIAN_LOADING](state: CustodianState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_CUSTODIANS](state: CustodianState, custodians) {
        state.custodians = custodians;
    },
    [types.SET_CUSTODIAN_INVITE](state: CustodianState, invite) {
        state.invite = invite;
    },
    [types.SET_CUSTODIAN_LISTINGS](state: CustodianState, listings) {
        state.listings = listings;
    },
    [types.SET_CUSTODIAN_BOOKINGS](state: CustodianState, bookings) {
        state.bookings = bookings;
    },
};

const actions = {


    /**
     * Get custodian
     */
    async join({commit}, custodian_id: string): Promise<{ joined?: boolean, error?: string }> {
        commit(types.SET_CUSTODIAN_LOADING, {subject: 'join', loading: true});

        try {
            await axios.get(`/v1/custodian-invite/${custodian_id}`);

            return {joined: true};
        } catch (e) {
            const message = (axios.isAxiosError(e)
                ? (e.response?.data as { message?: string })?.message
                : undefined) ?? 'There was an error';
            push.error({message});

            return {error: message};
        } finally {
            commit('SET_CUSTODIAN_LOADING', {subject: 'join', loading: false});
        }
    },


    /**
     * Create a custodian invite link
     */
    async createInvite({commit}, data): Promise<{ invite: any } | undefined> {
        commit('SET_CUSTODIAN_LOADING', {subject: 'create', loading: true});

        try {
            const response = await axios.post('/v1/custodian-invite', data);
            commit('SET_CUSTODIAN_INVITE', response?.data?.invite ?? []);

            return {invite: response?.data?.invite};
        } catch (e) {
            push.error({
                message: (axios.isAxiosError(e) ? (e.response?.data as {
                    message?: string
                })?.message : undefined) || 'There was an error',
            });

            return undefined;
        } finally {
            commit('SET_CUSTODIAN_LOADING', {subject: 'create', loading: false});
        }
    },

    /**
     * Update custodian
     */
    async updateCustodian({commit}, data): Promise<{ invite: any } | undefined> {
        commit('SET_CUSTODIAN_LOADING', {subject: 'create', loading: true});

        try {
            const response = await axios.put(`/v1/custodians/${data.custodian.uuid}`, data);
            commit('SET_CUSTODIAN_INVITE', response?.data?.invite ?? []);

            return {invite: response?.data?.invite};
        } catch (e) {
            push.error({
                message: (axios.isAxiosError(e) ? (e.response?.data as {
                    message?: string
                })?.message : undefined) || 'There was an error',
            });

            return undefined;
        } finally {
            commit('SET_CUSTODIAN_LOADING', {subject: 'create', loading: false});
        }
    },

    /**
     * Get custodian
     */
    getCustodians({commit}) {
        commit('SET_CUSTODIAN_LOADING', {subject: 'tax_categories', loading: true});

        axios.get('/v1/custodians')
            .then((response) => {
                commit('SET_CUSTODIANS', response?.data?.custodians ?? []);
            }).catch(() => {
        }).finally(() => {
            commit('SET_CUSTODIAN_LOADING', {subject: 'tax_categories', loading: false});
        });
    },

    /**
     * Get custodians listings
     */
    async getCustodianListings({commit}, custodian) {
        commit('SET_CUSTODIAN_LOADING', {subject: 'tax_categories', loading: true});

        try {
            try {
                const response = await axios.get(`/v1/custodians/${custodian.uuid}/listings`);
                const listings = response?.data?.listings ?? [];
                commit('SET_CUSTODIAN_LISTINGS', listings);
                return listings;
            } catch {
                return undefined;
            }
        } finally {
            commit('SET_CUSTODIAN_LOADING', {subject: 'tax_categories', loading: false});
        }
    },

    async getCustodianBookings({commit}, options) {
        commit('SET_CUSTODIAN_LOADING', {subject: 'tax_categories', loading: true});

        let qString = '?';
        if (options?.startDate) {
            qString += `startDate=${options.startDate}`;
        }
        if (options?.endDate) {
            qString += `&endDate=${options.endDate}`;
        }


        if (qString === '?') {
            qString = '';
        }

        try {
            try {
                const response = await axios.get(`/v1/custodians/${options.custodian_uuid}/bookings${qString}`);
                commit('SET_CUSTODIAN_BOOKINGS', response?.data?.bookings ?? []);
                return true;
            } catch {
                return false;
            }
        } finally {
            commit('SET_CUSTODIAN_LOADING', {subject: 'tax_categories', loading: false});
        }
    },

    /**
     * Delete custodian
     */
    async deleteCustodians({commit}, custodian_uuid): Promise<boolean> {
        commit('SET_CUSTODIAN_LOADING', {subject: 'delete', loading: true});

        try {
            await axios.delete(`/v1/custodians/${custodian_uuid}`);

            return true;
        } catch (e) {
            push.error({
                message: (axios.isAxiosError(e) ? (e.response?.data as {
                    message?: string
                })?.message : undefined) || 'There was an error',
            });

            return false;
        } finally {
            commit('SET_CUSTODIAN_LOADING', {subject: 'delete', loading: false});
        }
    },

    /**
     * Send custodian invite
     */
    async sendInviteEmailLink({commit}, data) {
        commit('SET_CUSTODIAN_LOADING', {subject: 'sending_invite_email', loading: true});

        try {
            try {
                await axios.post(`/v1/custodians/${data.custodian_uuid}/send-invite-email`, data);
                return true;
            } catch (e) {
                push.error({
                    message: (axios.isAxiosError(e) ? (e.response?.data as {
                        message?: string
                    })?.message : undefined) || 'There was an error',
                });
                return false;
            }
        } finally {
            commit('SET_CUSTODIAN_LOADING', {subject: 'sending_invite_email', loading: false});
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
